import { z } from 'zod';
import { NOTIFICATION_CHANNEL_TYPES } from './events.js';
import { ROLES } from './rbac.js';

/**
 * Contracts for the settings pages owned by the members / audit / notifications area:
 * members & roles, notification channels + per-event rules, delivery history, Telegram status.
 * Secrets (webhook URLs, codes, TOTP data, passkey keys) never appear in these DTOs.
 */

const Id = z.string().min(1).max(64);

// ---------- Members ----------
export const MemberStatus = z.enum(['active', 'invited', 'banned']);
export type MemberStatus = z.infer<typeof MemberStatus>;

/** One row of Settings → Members: an org member or a pending invitation. */
export const MemberDto = z.object({
  /** member.id, or invitation.id for `kind: 'invitation'`. */
  id: Id,
  kind: z.enum(['member', 'invitation']),
  userId: Id.nullable(),
  name: z.string(),
  email: z.string(),
  role: z.enum(ROLES),
  status: MemberStatus,
  twoFactorEnabled: z.boolean(),
  passkeys: z.number().int(),
  /** Most recent session activity (updatedAt/createdAt), falling back to the account timestamp. */
  lastLoginAt: z.string().nullable(),
  joinedAt: z.string().nullable(),
  /** Invitations only. */
  expiresAt: z.string().nullable(),
});
export type MemberDto = z.infer<typeof MemberDto>;

// ---------- Notification channels ----------
export const NotifyChannelType = z.enum(NOTIFICATION_CHANNEL_TYPES);

export const NotifyChannelDto = z.object({
  id: Id,
  type: NotifyChannelType,
  name: z.string(),
  enabled: z.boolean(),
  verified: z.boolean(),
  /** Non-secret display detail: e-mail address, masked Telegram chat, "••••abcd" webhook hint. */
  detail: z.string().nullable(),
  createdAt: z.string(),
});
export type NotifyChannelDto = z.infer<typeof NotifyChannelDto>;

const httpsUrl = z
  .string()
  .url()
  .max(500)
  .refine((u) => u.startsWith('https://'), 'must be an https:// URL');

export const NotifyChannelInput = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('email'),
    name: z.string().min(1).max(80).optional(),
    email: z.email().max(254),
  }),
  z.object({ type: z.literal('slack'), name: z.string().min(1).max(80).optional(), webhookUrl: httpsUrl }),
  z.object({ type: z.literal('discord'), name: z.string().min(1).max(80).optional(), webhookUrl: httpsUrl }),
  z.object({
    type: z.literal('apprise'),
    name: z.string().min(1).max(80).optional(),
    url: z.string().min(4).max(1000),
  }),
]);
export type NotifyChannelInput = z.infer<typeof NotifyChannelInput>;

export const NotifyChannelPatch = z.object({
  name: z.string().min(1).max(80).optional(),
  enabled: z.boolean().optional(),
});
export type NotifyChannelPatch = z.infer<typeof NotifyChannelPatch>;

export const ChannelVerifyInput = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'must be 6 digits'),
});
export const ChannelTestResult = z.object({ ok: z.boolean(), error: z.string().nullable() });
export type ChannelTestResult = z.infer<typeof ChannelTestResult>;

// ---------- Rules ----------
/** Events a user can route; `*` = every event. */
export const NOTIFIABLE_EVENTS = [
  'approval.requested',
  'human.question',
  'egress.requested',
  'run.failed',
  'run.completed',
  'task.done',
  'task.failed',
  'budget.warn',
  'budget.exceeded',
  'org.halted',
  'trigger.circuit_open',
  'mcp.auth_failed',
  'run_analysis.ready',
  'plan.limit',
] as const;

export const NOTIFIABLE_EVENT_LABELS: Record<(typeof NOTIFIABLE_EVENTS)[number] | '*', string> = {
  '*': 'All events',
  'approval.requested': 'Approval requested',
  'human.question': 'Agent asks a question',
  'egress.requested': 'Network access requested',
  'run.failed': 'Run failed',
  'run.completed': 'Run completed',
  'task.done': 'Task done',
  'task.failed': 'Task failed',
  'budget.warn': 'Budget warning',
  'budget.exceeded': 'Budget exceeded',
  'org.halted': 'Agents halted',
  'trigger.circuit_open': 'Trigger circuit breaker',
  'mcp.auth_failed': 'MCP server sign-in stopped working',
  'run_analysis.ready': 'Daily run analysis ready',
  'plan.limit': 'Plan limit reached (80% / 100%)',
};

const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');
export const QuietHours = z.object({ start: HHMM, end: HHMM, tz: z.string().min(1).max(64) });

export const NotifyRuleInput = z.object({
  /** null = all agents. */
  agentId: Id.nullable().default(null),
  eventType: z
    .string()
    .min(1)
    .max(64)
    .regex(/^(\*|[a-z_]+(\.\*|\.[a-z_]+)?)$/, 'event type, "<prefix>.*" or "*"'),
  /** Channel ids (or "inapp"). Empty = in-app only. */
  channelIds: z.array(Id).max(20).default([]),
  quietHours: QuietHours.nullable().default(null),
  /** false = mute this event. */
  enabled: z.boolean().default(true),
});
export type NotifyRuleInput = z.input<typeof NotifyRuleInput>;
export const NotifyRuleDto = NotifyRuleInput.extend({ id: Id, channelIds: z.array(Id) });
export type NotifyRuleDto = z.infer<typeof NotifyRuleDto>;
export const NotifyRulesReplace = z.object({ rules: z.array(NotifyRuleInput).max(100) });

// ---------- Delivery history ----------
export const DeliveryDto = z.object({
  notificationId: Id,
  eventType: z.string(),
  title: z.string(),
  createdAt: z.string(),
  deliveries: z.array(
    z.object({
      channelId: z.string(),
      channelType: z.string().nullable(),
      channelName: z.string().nullable(),
      ok: z.boolean(),
      error: z.string().nullable(),
      at: z.string(),
    }),
  ),
});
export type DeliveryDto = z.infer<typeof DeliveryDto>;

// ---------- Telegram ----------
export const TelegramStatusDto = z.object({
  /** TELEGRAM_BOT_TOKEN is set on the server. */
  configured: z.boolean(),
  botUsername: z.string().nullable(),
  /** TELEGRAM_WEBHOOK_SECRET is set (required to accept updates). */
  webhookSecretSet: z.boolean(),
  /** `${APP_ORIGIN}/api/hooks/telegram` */
  webhookUrl: z.string(),
  linked: z
    .object({ channelId: Id, chat: z.string(), enabled: z.boolean(), linkedAt: z.string() })
    .nullable(),
});
export type TelegramStatusDto = z.infer<typeof TelegramStatusDto>;
export const TelegramWebhookResult = z.object({ ok: z.boolean(), url: z.string() });
